import { useMemo, useRef, useState } from "react";

import { formatINR } from "../lib/format";

/** GitHub-style calendar heatmap of daily realized P&L over a backtest's full
 *  window. One column per week (Mon-anchored), one mini-grid per month, month
 *  label + that month's P&L total underneath. Hovering a day shows its date,
 *  realized P&L and fill count. Days are keyed by IST calendar date to match
 *  the trades table and golden-number day keys.
 *
 *  Cell color is a diverging scale: emerald arm for profit days, rose arm for
 *  loss days (3 magnitude steps each, bucketed against the run's largest
 *  absolute daily P&L), neutral grey for in-range days with no realized P&L.
 *  Dark mode uses its own steps from the same ramps (anchor flipped), not an
 *  automatic inversion.
 */

interface TradeLike {
  ts: string;
  realized_pnl_delta: string;
}

/** One pre-aggregated IST day (e.g. from /admin/users/{id}/pnl-daily). */
export interface DayLike {
  day: string; // "YYYY-MM-DD", IST
  realized_pnl: string | number;
  fills: number;
}

interface Props {
  /** Per-fill rows, bucketed here by IST day. Ignored when `days` is given. */
  trades?: TradeLike[];
  /** Already-aggregated days — used instead of `trades` for live/paper P&L. */
  days?: DayLike[];
  /** Window bounds — naive-UTC ISO strings (a backtest run's start/end). */
  startTs: string;
  endTs: string;
}

const IST_OFFSET_MS = 19_800_000; // +05:30

function parseTs(ts: string): number {
  if (!ts) return NaN;
  const hasTz = ts.endsWith("Z") || /[+-]\d{2}:?\d{2}$/.test(ts);
  return new Date(hasTz ? ts : `${ts}Z`).getTime();
}

/** IST calendar date of a timestamp, as a UTC-shifted Date (use getUTC*). */
function istDate(ts: string): Date {
  return new Date(parseTs(ts) + IST_OFFSET_MS);
}

function dayKey(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** Monday=0 … Sunday=6. */
function mondayIndex(d: Date): number {
  return (d.getUTCDay() + 6) % 7;
}

const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

// Diverging cell fills, weak→strong per arm. Each arm validated as an ordinal
// ramp (monotone lightness, visible step gaps, light end ≥2:1 on the surface)
// in both modes; dark picks its own steps rather than flipping the light ones.
const POS_FILL = [
  "bg-emerald-500 dark:bg-emerald-800",
  "bg-emerald-700 dark:bg-emerald-600",
  "bg-emerald-900 dark:bg-emerald-400",
];
const NEG_FILL = [
  "bg-rose-400 dark:bg-rose-800",
  "bg-rose-600 dark:bg-rose-600",
  "bg-rose-800 dark:bg-rose-400",
];
const NEUTRAL_FILL = "bg-surface-2";

interface DayCell {
  key: string; // "YYYY-MM-DD"
  label: string; // "Mon, 10 Nov 2025"
  pnl: number | undefined;
  fills: number;
  fillClass: string;
}

interface MonthGrid {
  label: string; // "Nov 25"
  total: number;
  hasDays: boolean;
  /** columns[week][row] — row indexed Mon=0; null = outside month/window. */
  columns: (DayCell | null)[][];
}

interface Tooltip {
  left: number;
  top: number;
  cell: DayCell;
}

export default function PnlCalendar({ trades, days, startTs, endTs }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [tooltip, setTooltip] = useState<Tooltip | null>(null);

  const { months, rows, startKey, endKey } = useMemo(() => {
    const pnlByDay = new Map<string, number>();
    const fillsByDay = new Map<string, number>();
    if (days) {
      for (const d of days) {
        const v = Number(d.realized_pnl);
        pnlByDay.set(d.day, Number.isFinite(v) ? v : 0);
        fillsByDay.set(d.day, d.fills);
      }
    } else {
      for (const t of trades ?? []) {
        const key = dayKey(istDate(t.ts));
        const delta = Number(t.realized_pnl_delta);
        pnlByDay.set(key, (pnlByDay.get(key) ?? 0) + (Number.isFinite(delta) ? delta : 0));
        fillsByDay.set(key, (fillsByDay.get(key) ?? 0) + 1);
      }
    }

    const start = istDate(startTs);
    const end = istDate(endTs);
    const startK = dayKey(start);
    const endK = dayKey(end);

    let maxAbs = 0;
    for (const v of pnlByDay.values()) maxAbs = Math.max(maxAbs, Math.abs(v));

    // Collapse the weekend rows when no weekend day carries P&L (NSE runs);
    // 24/7 markets (crypto/FX) keep all 7 rows.
    let weekendActive = false;
    for (const key of pnlByDay.keys()) {
      const dow = mondayIndex(new Date(`${key}T00:00:00Z`));
      if (dow >= 5 && (pnlByDay.get(key) ?? 0) !== 0) {
        weekendActive = true;
        break;
      }
    }
    const rowCount = weekendActive ? 7 : 5;

    const grids: MonthGrid[] = [];
    const cursor = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1));
    const endMonth = Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), 1);
    while (cursor.getTime() <= endMonth) {
      const y = cursor.getUTCFullYear();
      const m = cursor.getUTCMonth();
      const daysInMonth = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
      const columns: (DayCell | null)[][] = [];
      let column: (DayCell | null)[] = new Array(rowCount).fill(null);
      let total = 0;
      let hasDays = false;

      for (let day = 1; day <= daysInMonth; day++) {
        const d = new Date(Date.UTC(y, m, day));
        const row = mondayIndex(d);
        if (row >= rowCount) continue;
        if (day > 1 && row === 0) {
          columns.push(column);
          column = new Array(rowCount).fill(null);
        }
        const key = dayKey(d);
        if (key < startK || key > endK) continue;
        hasDays = true;
        const pnl = pnlByDay.get(key);
        total += pnl ?? 0;
        let fillClass = NEUTRAL_FILL;
        if (pnl !== undefined && pnl !== 0 && maxAbs > 0) {
          const tier = Math.min(2, Math.floor((Math.abs(pnl) / maxAbs) * 3));
          fillClass = pnl > 0 ? POS_FILL[tier] : NEG_FILL[tier];
        }
        column[row] = {
          key,
          label: d.toLocaleDateString("en-IN", {
            weekday: "short",
            day: "numeric",
            month: "short",
            year: "numeric",
            timeZone: "UTC",
          }),
          pnl,
          fills: fillsByDay.get(key) ?? 0,
          fillClass,
        };
      }
      columns.push(column);
      grids.push({
        label: `${MONTH_NAMES[m]} ${String(y).slice(2)}`,
        total,
        hasDays,
        columns,
      });
      cursor.setUTCMonth(m + 1);
    }

    return { months: grids.filter((g) => g.hasDays), rows: rowCount, startKey: startK, endKey: endK };
  }, [trades, days, startTs, endTs]);

  function showTooltip(e: React.MouseEvent<HTMLDivElement>, cell: DayCell) {
    // Viewport-fixed so the horizontally-scrolling grid can never clip it;
    // clamped so edge cells don't push it off screen.
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({
      left: Math.min(Math.max(90, rect.left + rect.width / 2), window.innerWidth - 90),
      top: rect.bottom + 6,
      cell,
    });
  }

  if (months.length === 0)
    return <div className="text-sm text-muted">No fills to plot.</div>;

  return (
    <div className="space-y-3">
      <div className="text-xs text-muted font-mono text-right">
        {startKey} → {endKey} (IST days)
      </div>
      <div ref={containerRef} className="relative overflow-x-auto pb-1">
        {tooltip && (
          <div
            className="fixed z-50 -translate-x-1/2 pointer-events-none
                       rounded border border-border bg-surface shadow-elev px-2.5 py-1.5
                       text-xs whitespace-nowrap"
            style={{ left: tooltip.left, top: tooltip.top }}
          >
            <div className="text-fg-subtle">{tooltip.cell.label}</div>
            {tooltip.cell.pnl === undefined ? (
              <div className="text-muted">No realized P&L</div>
            ) : (
              <div
                className={`font-mono font-medium ${
                  tooltip.cell.pnl > 0
                    ? "text-up-700 dark:text-up-400"
                    : tooltip.cell.pnl < 0
                      ? "text-down-700 dark:text-down-400"
                      : "text-fg"
                }`}
              >
                {formatINR(tooltip.cell.pnl, { sign: true })}
                <span className="text-muted font-sans font-normal">
                  {" "}· {tooltip.cell.fills} fill{tooltip.cell.fills === 1 ? "" : "s"}
                </span>
              </div>
            )}
          </div>
        )}
        <div className="flex gap-4 w-max">
          {months.map((month) => (
            <div key={month.label} className="flex flex-col gap-1.5">
              <div className="flex gap-0.5">
                {month.columns.map((column, ci) => (
                  <div key={ci} className="flex flex-col gap-0.5">
                    {column.map((cell, ri) =>
                      cell ? (
                        <div
                          key={ri}
                          className={`w-3 h-3 rounded-[3px] ${cell.fillClass}
                                      hover:ring-2 hover:ring-brand-400 cursor-default`}
                          onMouseEnter={(e) => showTooltip(e, cell)}
                          onMouseLeave={() => setTooltip(null)}
                        />
                      ) : (
                        <div key={ri} className="w-3 h-3" />
                      ),
                    )}
                  </div>
                ))}
              </div>
              <div className="text-xs text-muted">{month.label}</div>
              <div
                className={`text-xs font-mono ${
                  month.total > 0
                    ? "text-up-700 dark:text-up-400"
                    : month.total < 0
                      ? "text-down-700 dark:text-down-400"
                      : "text-muted"
                }`}
              >
                {formatINR(month.total, { sign: true, decimals: 0 })}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-1 text-xs text-muted">
        <span className="mr-1">Loss</span>
        {[...NEG_FILL].reverse().map((c) => (
          <span key={c} className={`w-3 h-3 rounded-[3px] ${c}`} />
        ))}
        <span className={`w-3 h-3 rounded-[3px] ${NEUTRAL_FILL}`} />
        {POS_FILL.map((c) => (
          <span key={c} className={`w-3 h-3 rounded-[3px] ${c}`} />
        ))}
        <span className="ml-1">Profit</span>
        <span className="ml-3 text-fg-subtle">
          Daily realized P&L (fees included), IST days. {rows === 5 ? "Mon–Fri." : "All days."}
        </span>
      </div>
    </div>
  );
}
